import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BrandMarkIcon, CheckIcon, ChevronRightIcon } from '@/components/Icons';
import { fetchFeedbackForm, submitFeedbackForm, type FeedbackFormData, type FeedbackSubmission } from '@/lib/api';

// ─── Types ───────────────────────────────────────────────────────────────────

type RatingKey = 'service_quality' | 'staff_behavior' | 'cleanliness' | 'waiting_time' | 'value_for_money';

type RatingCriteria = Record<RatingKey, number>;

const RATING_CRITERIA: { key: RatingKey; label: string }[] = [
  { key: 'service_quality', label: 'Service Quality' },
  { key: 'staff_behavior', label: 'Staff Behavior' },
  { key: 'cleanliness', label: 'Cleanliness & Hygiene' },
  { key: 'waiting_time', label: 'Waiting Time' },
  { key: 'value_for_money', label: 'Value for Money' },
];

const RATING_LABELS = ['1 Poor', '2 Below\nExpectations', '3 Met\nExpectations', '4 Above\nExpectations', '5\nOutstanding'];

const RECOMMEND_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'Not likely at all' },
  { value: 2, label: 'Unlikely' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Likely' },
  { value: 5, label: 'Very likely' },
];

const LIKED_MOST_OPTIONS = [
  'Service quality',
  'Staff friendliness',
  'Cleanliness',
  'Value for money',
  'Speed of service',
  'Overall experience',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deriveOverallRating(criteria: Partial<RatingCriteria>): number {
  const values = Object.values(criteria).filter((v) => typeof v === 'number' && v >= 1);
  if (values.length === 0) return 0;
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length);
}

function deduceFeedbackLabel(rating: number): string {
  if (rating >= 4) return 'positive';
  if (rating === 3) return 'neutral';
  return 'negative';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function RatingMatrix({
  criteria,
  onChange,
  disabled,
}: {
  criteria: Partial<RatingCriteria>;
  onChange: (key: RatingKey, value: number) => void;
  disabled: boolean;
}) {
  return (
    <>
      <div className="fb-rating-table">
        <div className="fb-rating-header">
          <div className="fb-rating-label-col" />
          {RATING_LABELS.map((label, i) => (
            <div key={i} className="fb-rating-col">
              {label.split('\n').map((line, j) => (
                <span key={j}>{line}</span>
              ))}
            </div>
          ))}
        </div>

        {RATING_CRITERIA.map(({ key, label }) => (
          <div key={key} className={`fb-rating-row${criteria[key] ? ' fb-rating-row--selected' : ''}`}>
            <div className="fb-rating-label-col">{label}</div>
            {[1, 2, 3, 4, 5].map((value) => (
              <div key={value} className="fb-rating-col">
                <button
                  type="button"
                  aria-label={`${label}: ${RATING_LABELS[value - 1]}`}
                  className={`fb-radio${criteria[key] === value ? ' fb-radio--active' : ''}`}
                  disabled={disabled}
                  onClick={() => onChange(key, value)}>
                  <span className="fb-radio-inner" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="fb-rating-mobile">
        {RATING_CRITERIA.map(({ key, label }) => (
          <div key={key} className={`fb-rating-mobile-card${criteria[key] ? ' fb-rating-mobile-card--selected' : ''}`}>
            <p className="fb-rating-mobile-label">{label}</p>
            <div className="fb-rating-mobile-options" role="group" aria-label={label}>
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-label={`${label}: ${RATING_LABELS[value - 1].replace(/\n/g, ' ')}`}
                  aria-pressed={criteria[key] === value}
                  className={`fb-score-btn${criteria[key] === value ? ' fb-score-btn--active' : ''}`}
                  disabled={disabled}
                  onClick={() => onChange(key, value)}>
                  {value}
                </button>
              ))}
            </div>
            <p className="fb-rating-mobile-hint">1 Poor · 5 Outstanding</p>
          </div>
        ))}
      </div>
    </>
  );
}

function ServiceCheckboxes({
  available,
  selected,
  onChange,
  disabled,
}: {
  available: string[];
  selected: string[];
  onChange: (services: string[]) => void;
  disabled: boolean;
}) {
  function toggle(service: string) {
    if (selected.includes(service)) {
      onChange(selected.filter((s) => s !== service));
    } else {
      onChange([...selected, service]);
    }
  }

  return (
    <div className="fb-checkbox-list">
      {available.map((service) => {
        const checked = selected.includes(service);
        return (
          <label key={service} className={`fb-checkbox-item${checked ? ' fb-checkbox-item--checked' : ''}`}>
            <span className={`fb-checkbox${checked ? ' fb-checkbox--checked' : ''}`} aria-hidden="true">
              {checked && <CheckIcon size={12} />}
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              disabled={disabled}
              onChange={() => toggle(service)}
            />
            {service}
          </label>
        );
      })}
    </div>
  );
}

function NativeSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string | number | null;
  onChange: (v: string) => void;
  options: { value: string | number; label: string }[];
  placeholder: string;
  disabled: boolean;
}) {
  return (
    <div className="fb-select-wrapper">
      <select
        className="fb-select"
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronRightIcon size={16} className="fb-select-chevron" />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'form' | 'already_submitted' | 'success';

export function FeedbackPage() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<FeedbackFormData | null>(null);

  // Form state
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [criteria, setCriteria] = useState<Partial<RatingCriteria>>({});
  const [recommend, setRecommend] = useState<number | null>(null);
  const [likedMost, setLikedMost] = useState<string>('');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const load = useCallback(
    async (signal: AbortSignal) => {
      if (!token) {
        setErrorMessage('Invalid feedback link.');
        setPageState('error');
        return;
      }

      try {
        const data = await fetchFeedbackForm(token, signal);
        setFormData(data);

        if (data.feedback) {
          // Already submitted — show read-only view
          setPageState('already_submitted');
          return;
        }

        if (data.appointment.status === 'cancelled') {
          setErrorMessage('This appointment was cancelled and cannot receive feedback.');
          setPageState('error');
          return;
        }

        // Pre-select all appointment services
        setSelectedServices(data.appointment.services ?? []);
        setPageState('form');
      } catch (err) {
        if ((err as { name?: string }).name === 'AbortError') return;
        setErrorMessage(
          err instanceof Error && err.message ? err.message : 'Could not load the feedback form. Please try again.'
        );
        setPageState('error');
      }
    },
    [token]
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  function handleCriteriaChange(key: RatingKey, value: number) {
    setCriteria((prev) => ({ ...prev, [key]: value }));
    setFieldError(null);
  }

  async function handleSubmit() {
    if (!token || isSubmitting) return;

    // Validate all 5 criteria are rated
    const missing = RATING_CRITERIA.filter(({ key }) => !criteria[key]);
    if (missing.length > 0) {
      setFieldError(`Please rate all criteria. Missing: ${missing.map((m) => m.label).join(', ')}.`);
      return;
    }

    const overallRating = deriveOverallRating(criteria);
    if (overallRating < 1) {
      setFieldError('Please complete the rating matrix.');
      return;
    }

    setIsSubmitting(true);
    setFieldError(null);

    const submission: FeedbackSubmission = {
      feedback: deduceFeedbackLabel(overallRating),
      rating: overallRating,
      ratings: criteria as RatingCriteria,
      services: selectedServices,
      recommend: recommend ?? undefined,
      liked_most: likedMost,
      comment,
    };

    try {
      await submitFeedbackForm(token, submission);
      setPageState('success');
    } catch (err) {
      setFieldError(
        err instanceof Error && err.message ? err.message : 'Submission failed. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  // ── Renders ────────────────────────────────────────────────────────────────

  const clientName = formData?.client?.name ?? 'there';

  if (pageState === 'loading') {
    return (
      <div className="fb-root">
        <div className="fb-shell">
          <div className="fb-card">
            <div className="fb-brand">
              <BrandMarkIcon />
            </div>
            <p className="fb-loading-text">Loading your feedback form…</p>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="fb-root">
        <div className="fb-shell">
          <div className="fb-card">
            <div className="fb-brand">
              <BrandMarkIcon />
            </div>
            <h1 className="fb-title">Feedback unavailable</h1>
            <p className="fb-body">{errorMessage ?? 'This feedback link is invalid or has expired.'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'already_submitted') {
    const fb = formData?.feedback;
    return (
      <div className="fb-root">
        <div className="fb-shell">
          <div className="fb-card">
            <div className="fb-brand">
              <BrandMarkIcon />
            </div>
            <div className="fb-success-icon" aria-hidden="true">✓</div>
            <h1 className="fb-title">Already submitted</h1>
            <p className="fb-body">
              Thank you, <strong>{clientName}</strong>. You already submitted feedback for this appointment.
            </p>
            {fb && (
              <div className="fb-submitted-summary">
                <div className="fb-summary-row">
                  <span className="fb-summary-label">Overall rating</span>
                  <span className="fb-summary-value">{'★'.repeat(fb.rating)}{'☆'.repeat(5 - fb.rating)}</span>
                </div>
                {fb.comment && (
                  <div className="fb-summary-row">
                    <span className="fb-summary-label">Comment</span>
                    <span className="fb-summary-value">{fb.comment}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (pageState === 'success') {
    return (
      <div className="fb-root">
        <div className="fb-shell">
          <div className="fb-card">
            <div className="fb-brand">
              <BrandMarkIcon />
            </div>
            <div className="fb-success-icon" aria-hidden="true">✓</div>
            <h1 className="fb-title">Thank you, {clientName}!</h1>
            <p className="fb-body">Your feedback has been recorded. We appreciate you taking the time to share your experience.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  const appointmentServices = formData?.appointment?.services ?? [];

  return (
    <div className="fb-root">
      <div className="fb-shell">
        {/* Header */}
        <header className="fb-header">
          <div className="fb-brand">
            <BrandMarkIcon />
          </div>
          <div className="fb-header-copy">
            <h1 className="fb-page-title">Share Your Experience</h1>
            <p className="fb-page-subtitle">
              Hi <strong>{clientName}</strong>, we'd love to hear how your visit went.
            </p>
          </div>
        </header>

        {/* Section: Services received */}
        {appointmentServices.length > 0 && (
          <section className="fb-card">
            <h2 className="fb-section-title">
              Which services did you receive? <span className="fb-required" aria-hidden="true">*</span>
            </h2>
            <ServiceCheckboxes
              available={appointmentServices}
              selected={selectedServices}
              onChange={setSelectedServices}
              disabled={isSubmitting}
            />
          </section>
        )}

        {/* Section: Criteria ratings */}
        <section className="fb-card">
          <h2 className="fb-section-title">
            How would you rate the following? <span className="fb-required" aria-hidden="true">*</span>
          </h2>
          <RatingMatrix criteria={criteria} onChange={handleCriteriaChange} disabled={isSubmitting} />
        </section>

        {/* Section: Recommend */}
        <section className="fb-card">
          <h2 className="fb-section-title">
            How likely are you to recommend Slot Look to friends or family? <span className="fb-required" aria-hidden="true">*</span>
          </h2>
          <NativeSelect
            value={recommend}
            onChange={(v) => setRecommend(Number(v))}
            options={RECOMMEND_OPTIONS}
            placeholder="Choose"
            disabled={isSubmitting}
          />
        </section>

        {/* Section: Liked most */}
        <section className="fb-card">
          <h2 className="fb-section-title">
            What did you like the most? <span className="fb-required" aria-hidden="true">*</span>
          </h2>
          <NativeSelect
            value={likedMost}
            onChange={setLikedMost}
            options={LIKED_MOST_OPTIONS.map((o) => ({ value: o, label: o }))}
            placeholder="Choose"
            disabled={isSubmitting}
          />
        </section>

        {/* Section: Comment */}
        <section className="fb-card">
          <h2 className="fb-section-title">Any additional comments?</h2>
          <textarea
            className="fb-textarea"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share any thoughts you have…"
            rows={4}
            maxLength={1000}
            disabled={isSubmitting}
          />
          <p className="fb-char-count">{comment.length} / 1000</p>
        </section>

        {/* Error */}
        {fieldError && (
          <div className="fb-error-notice" role="alert">
            {fieldError}
          </div>
        )}

        {/* Submit */}
        <div className="fb-submit-row">
          <button
            type="button"
            className="fb-submit-btn"
            onClick={handleSubmit}
            disabled={isSubmitting}>
            {isSubmitting ? 'Submitting…' : 'Submit Feedback'}
            {!isSubmitting && <ChevronRightIcon size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
